import {
  DSChip,
  DSTextarea,
} from "@components/design-system/molecules";
import { DSDiffView } from "@components/design-system/organisms/DSNoteInputDiffView";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import mixpanel from "mixpanel-browser";
import {
  Controller,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { PREFERENCES_PREFILLS } from "../../../constants";

export default function PreferencesSection({
  control,
  readOnly = false,
}) {
  const { setValue } = useFormContext();
  const currentValue = useWatch({ control, name: "userPreferences" });
  const templateUuid = useWatch({ control, name: "uuid" });

  const wa = useWritingAssistant({
    entityType: "template",
    entityUuid: templateUuid || "",
    fieldName: "USER_PREFERENCES",
    getFieldValue: () =>
      typeof currentValue === "string" ? currentValue : "",
    setFieldValue: (v) =>
      setValue("userPreferences", v, { shouldDirty: true }),
    languageAiAssistantEnabled: false,
    subheadingsAiAssistantEnabled: false,
  });

  const handleChipClick = (prefill, chipLabel) => {
    if (readOnly) return;

    const newValue = currentValue
      ? `${currentValue} ${prefill.description}`
      : prefill.description;

    setValue("userPreferences", newValue);
    mixpanel.track("Preference chip clicked", {
      templateUuid,
      chipLabel,
    });
  };

  const showPrefills = !readOnly && !wa.diffState;

  return (
    <>
      <label
        htmlFor="userPreferences"
        className="block text-sm font-semibold text-primary mb-1"
      >
        Preferences
      </label>
      {showPrefills && (
        <div className="flex flex-wrap items-center gap-sm mb-sm">
          <FontAwesomeIcon
            icon={faSparkles}
            size="lg"
            className="text-yellow-400"
          />{" "}
          {Object.entries(PREFERENCES_PREFILLS).map(
            ([key, value]) => (
              <DSChip
                key={key}
                variant="selectable"
                onClick={() => handleChipClick(value, key)}
                dataTestId={`preference-${key}`}
              >
                {key}
              </DSChip>
            ),
          )}
        </div>
      )}
      {!readOnly && wa.diffState ? (
        <DSDiffView
          afterContent={wa.diffState.after}
          beforeContent={wa.diffState.before}
          section="freeform"
          label="Preferences"
          isLoading={wa.diffState.isLoading}
          onAccept={wa.acceptChanges}
          onReject={wa.rejectChanges}
          dataTestId="user-preferences-diff"
          plainText
        />
      ) : (
        <Controller
          control={control}
          name="userPreferences"
          render={({ field: { onChange, value, ref } }) => (
            <DSTextarea
              id="userPreferences"
              name="userPreferences"
              dataTestId="user-preferences"
              minRows={3}
              placeholder={`Define the tone and voice of your record or note. Give formatting or stylistic instructions e.g., 'Write in full sentences,' 'Use bullet points,' or 'Keep the tone concise and professional.'
\nYou can also click any of the example chips above to quickly add instructions to this section.

Example:
Use short paragraphs and medical abbreviations when appropriate.`}
              // hack to dodge js errors because react-hook-form sometimes passes an empty object as the value
              value={
                ["string", "number"].includes(typeof value)
                  ? value
                  : ""
              }
              onChange={(e) => {
                onChange(e);
              }}
              ref={ref}
              disabled={readOnly}
            />
          )}
        />
      )}
    </>
  );
}
